import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronRight, Mail } from "lucide-react";

import { getRecibidasPendingAckCount } from "@/services/invoiceService";

/**
 * Banner on the dashboard surfacing the count of received CFEs awaiting
 * the receptor's accept/reject decision (DGI §XVIII, 20-día window).
 * Three intensity bands:
 *
 *   - overdue (any past day 20): red, "atención" tone
 *   - dueSoon (any day 17-19, none overdue): amber, "vencen pronto"
 *   - normal (count > 0 but all < day 17): neutral teal, "tenés N por responder"
 *
 * Hidden when count = 0 (the most common state). Polls every 5 minutes
 * to catch new EnvioCFE arrivals without forcing a page reload.
 */
export function PendingRecibidaAckBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ["recibidas", "pending-ack-count"],
    queryFn: getRecibidasPendingAckCount,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const variant = useMemo(() => {
    if (!data || data.total === 0) return null;
    if (data.overdueCount > 0) return "overdue" as const;
    if (data.dueSoonCount > 0) return "dueSoon" as const;
    return "normal" as const;
  }, [data]);

  if (!data || data.total === 0 || !variant) return null;

  const message = (() => {
    if (variant === "overdue") {
      return t(
        "dashboard.pendingRecibidaAck.overdue",
        "Tenés {{n}} comprobante(s) que ya vencieron el plazo de respuesta DGI. Fiscalo los aceptará automáticamente si no actuás.",
        { n: data.total },
      );
    }
    if (variant === "dueSoon") {
      return t(
        "dashboard.pendingRecibidaAck.dueSoon",
        "Tenés {{n}} comprobante(s) por responder. {{soon}} vencen el plazo DGI en menos de 3 días.",
        { n: data.total, soon: data.dueSoonCount },
      );
    }
    return t(
      "dashboard.pendingRecibidaAck.normal",
      "Tenés {{n}} comprobante(s) recibido(s) esperando aceptación o rechazo.",
      { n: data.total },
    );
  })();

  const palette = (() => {
    switch (variant) {
      case "overdue":
        return {
          wrap:
            "border-[rgba(201,94,62,0.40)] bg-[rgba(201,94,62,0.10)] text-[var(--fx-warn-text)]",
          icon: AlertTriangle,
          iconColor: "text-[var(--fx-warn-text)]",
        };
      case "dueSoon":
        return {
          wrap:
            "border-[#E8C490] bg-[rgba(232,196,144,0.18)] text-[#8C5A1F] dark:bg-[rgba(232,196,144,0.10)] dark:text-[#E8C490]",
          icon: AlertTriangle,
          iconColor: "text-[#8C5A1F] dark:text-[#E8C490]",
        };
      case "normal":
        return {
          wrap:
            "border-[var(--fx-ink-200)] bg-[rgba(45,97,73,0.06)] text-[var(--fx-accent-700)] dark:border-[rgba(111,165,133,0.30)] dark:bg-[rgba(111,165,133,0.10)] dark:text-[var(--fx-accent-300)]",
          icon: Mail,
          iconColor: "text-[var(--fx-accent-700)] dark:text-[var(--fx-accent-300)]",
        };
    }
  })();

  const Icon = palette.icon;

  return (
    <Link
      to="/invoices/recibidas?pendingAck=1"
      className={`flex items-center justify-between gap-3 rounded-[var(--fx-r-md)] border px-4 py-3 text-sm transition-colors hover:bg-opacity-80 ${palette.wrap}`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${palette.iconColor}`} />
        <span>{message}</span>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0" />
    </Link>
  );
}
